import { useState, useCallback, useEffect, useMemo } from 'react';
import { Droplets, Syringe, Pill, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { TreatmentCurveChart } from '../ui/TreatmentCurveChart';
import type { TreatmentType, Phase, PhaseOverrides, PhaseRatios, Platform } from '../../types';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useVisualEffects } from '../../contexts/VisualEffectsContext';
import {
  TREATMENT_LABELS,
  MIN_DURATION_MINUTES,
  MAX_DURATION_MINUTES,
  DEFAULT_DURATIONS,
  PHASE_RATIOS,
  PHASES_IN_ORDER,
  PHASE_LABELS,
  PHASE_DESCRIPTIONS,
} from '../../lib/constants';
import { formatMinutes } from '../../lib/playlist-generator';
import { APPLE_AFFILIATE_ENABLED, getAppleMusicSubscribeUrl } from '../../lib/apple-music-affiliate';

interface EntryScreenProps {
  selectedPlatform: Platform;
  onSelectPlatform: (platform: Platform) => void;
  onBeginSession: (treatmentType: TreatmentType, durationMinutes: number, phaseOverrides?: PhaseOverrides) => void;
  isLoading: boolean;
  error: string | null;
  initialTreatmentType: TreatmentType;
  isAppleMusicAuthorized: boolean;
}

const treatmentIcons: Record<TreatmentType, React.ReactNode> = {
  iv: <Droplets className="w-5 h-5" />,
  im: <Syringe className="w-5 h-5" />,
  oral: <Pill className="w-5 h-5" />,
  spravato: <Sparkles className="w-5 h-5" />,
};

function getDefaultPhasePercentages(treatmentType: TreatmentType): Record<Phase, number> {
  const ratios = PHASE_RATIOS[treatmentType];
  const percentages: Record<Phase, number> = {
    opening: Math.round(ratios.opening * 100),
    building: Math.round(ratios.building * 100),
    peak: Math.round(ratios.peak * 100),
    longTail: Math.round(ratios.longTail * 100),
    closing: Math.round(ratios.closing * 100),
  };

  const total = Object.values(percentages).reduce((sum, val) => sum + val, 0);
  if (total !== 100) {
    const largestPhase = PHASES_IN_ORDER.reduce((max, phase) =>
      percentages[phase] > percentages[max] ? phase : max
    );
    percentages[largestPhase] += 100 - total;
  }

  return percentages;
}

export function EntryScreen({
  selectedPlatform,
  onSelectPlatform,
  onBeginSession,
  isLoading,
  error,
  initialTreatmentType,
  isAppleMusicAuthorized,
}: EntryScreenProps) {
  const { settings: visualEffects } = useVisualEffects();
  const enhanced = visualEffects.enhancedAnimations;
  // const [logoEntranceDone, setLogoEntranceDone] = useState(false);

  // const handleLogoAnimationEnd = useCallback((e: React.AnimationEvent) => {
  //   if (e.animationName === 'logo-drop-in') setLogoEntranceDone(true);
  // }, []);

  const [treatmentType, setTreatmentType] = useState<TreatmentType>(initialTreatmentType);
  const [duration, setDuration] = useState(DEFAULT_DURATIONS[initialTreatmentType]);
  const [showAdvanced, setShowAdvanced] = useLocalStorage('healfm-show-advanced', false);
  const [phasePercentages, setPhasePercentages] = useState<Record<Phase, number>>(() =>
    getDefaultPhasePercentages(initialTreatmentType)
  );

  const totalPercentage = useMemo(() => {
    return Object.values(phasePercentages).reduce((sum, val) => sum + val, 0);
  }, [phasePercentages]);

  const isValidTotal = totalPercentage === 100;
  const canSubmit = selectedPlatform !== 'none' && (!showAdvanced || isValidTotal);

  const phaseRatios = useMemo<PhaseRatios | undefined>(() => {
    if (!showAdvanced) return undefined;
    const total = Object.values(phasePercentages).reduce((sum, val) => sum + val, 0);
    if (total <= 0) return undefined;
    return {
      opening: phasePercentages.opening / total,
      building: phasePercentages.building / total,
      peak: phasePercentages.peak / total,
      longTail: phasePercentages.longTail / total,
      closing: phasePercentages.closing / total,
    };
  }, [phasePercentages, showAdvanced]);

  useEffect(() => {
    setDuration(DEFAULT_DURATIONS[treatmentType]);
  }, [treatmentType]);

  useEffect(() => {
    setPhasePercentages(getDefaultPhasePercentages(treatmentType));
  }, [treatmentType]);

  const handlePhaseChange = useCallback((phase: Phase, value: number) => {
    setPhasePercentages(prev => ({ ...prev, [phase]: value }));
  }, []);

  const handleAutoAdjust = useCallback(() => {
    const diff = 100 - totalPercentage;
    if (diff === 0) return;

    const total = Object.values(phasePercentages).reduce((sum, val) => sum + val, 0);
    const adjusted = { ...phasePercentages };
    let remaining = diff;
    const phases = PHASES_IN_ORDER.slice();

    for (let i = 0; i < phases.length - 1; i++) {
      const phase = phases[i];
      const proportion = phasePercentages[phase] / total;
      const adjustment = Math.round(diff * proportion);
      adjusted[phase] = Math.max(5, Math.min(60, phasePercentages[phase] + adjustment));
      remaining -= (adjusted[phase] - phasePercentages[phase]);
    }

    const lastPhase = phases[phases.length - 1];
    adjusted[lastPhase] = Math.max(5, Math.min(60, phasePercentages[lastPhase] + remaining));
    setPhasePercentages(adjusted);
  }, [phasePercentages, totalPercentage]);

  const handleResetToDefaults = useCallback(() => {
    setPhasePercentages(getDefaultPhasePercentages(treatmentType));
  }, [treatmentType]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const overrides = showAdvanced ? phasePercentages : undefined;
      onBeginSession(treatmentType, duration, overrides);
    },
    [treatmentType, duration, showAdvanced, phasePercentages, onBeginSession]
  );

  const getPhaseMinutes = (percentage: number) => {
    return formatMinutes((percentage / 100) * duration);
  };

  const platformOptions: { value: Platform; label: string; sublabel: string; icon: React.ReactNode }[] = [
    {
      value: 'youtube',
      label: 'YouTube',
      sublabel: 'No account needed',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.6 12 3.6 12 3.6s-7.6 0-9.4.5A3 3 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12c0 1.9.2 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.8.5 9.4.5 9.4.5s7.6 0 9.4-.5a3 3 0 0 0 2.1-2.1c.3-1.9.5-3.9.5-5.8s-.2-3.9-.5-5.8zM9.6 15.5V8.5l6.2 3.5-6.2 3.5z" />
        </svg>
      ),
    },
    {
      value: 'apple-music',
      label: 'Apple Music',
      sublabel: isAppleMusicAuthorized ? 'Connected' : 'Requires subscription',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
        </svg>
      ),
    },
    {
      value: 'preview',
      label: 'Preview',
      sublabel: '30s clips, no account',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 12h.01M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728M8.464 15.536a5 5 0 010-7.072" />
        </svg>
      ),
    },
  ];

  const formatDuration = (minutes: number): { value: number; unit: string }[] => {
    if (minutes < 60) return [{ value: minutes, unit: 'min' }];
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return [{ value: h, unit: 'h' }];
    return [{ value: h, unit: 'h' }, { value: m, unit: 'min' }];
  };

  const treatmentTypes: TreatmentType[] = ['iv', 'im', 'oral', 'spravato'];

  return (
    <div className={`flex-1 flex flex-col items-center px-4 py-6 sm:px-6 sm:py-10 ${enhanced ? '' : 'animate-screen-enter'}`}>
      <div className="w-full max-w-xl">
        {/* Brand Hero */}
        <div className="text-center mb-8 sm:mb-12">
          {/* <div
            className={`mb-6 sm:mb-8 relative inline-block ${enhanced ? (logoEntranceDone ? 'animate-float' : 'animate-logo-drop-in') : 'animate-float'}`}
            onAnimationEnd={enhanced ? handleLogoAnimationEnd : undefined}
          >
            <div className="absolute inset-0 -m-4 flex items-center justify-center">
              <div
                className={`absolute w-28 h-28 rounded-full border border-ember-500/15 dark:border-ember-500/10 ${enhanced ? 'animate-ring-expand' : 'animate-pulse-slow'}`}
                style={enhanced ? { animationDelay: '200ms' } : undefined}
              />
              <div
                className={`absolute w-36 h-36 rounded-full border border-glow-500/10 dark:border-glow-500/8 ${enhanced ? 'animate-ring-expand' : 'animate-pulse-slow'}`}
                style={{ animationDelay: enhanced ? '400ms' : '0.5s' }}
              />
            </div>
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl shadow-2xl shadow-ember-500/20 overflow-hidden">
              <img
                src="/logo-512.png"
                alt="heal.fm logo"
                className="w-full h-full rounded-2xl object-cover scale-[1.08]"
                loading="eager"
              />
            </div>
          </div> */}
          <h1
            className={`font-serif text-5xl sm:text-7xl font-semibold mb-3 tracking-tight ${enhanced ? 'animate-title-fade-up' : ''}`}
            style={enhanced ? { animationDelay: '400ms' } : undefined}
          >
            <span className="gradient-text">heal.fm</span>
          </h1>
          <p
            className={`font-serif text-xl sm:text-2xl text-parchment-text/70 dark:text-earth-200/70 italic ${enhanced ? 'animate-text-reveal' : ''}`}
            style={enhanced ? { animationDelay: '600ms' } : undefined}
          >
            Music for the journey within
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className={`space-y-6 sm:space-y-8 ${enhanced ? 'animate-button-slide-up' : ''}`}
          style={enhanced ? { animationDelay: '750ms' } : undefined}
        >
          {/* Platform Selection */}
          <section>
            <h2 className="text-xs uppercase tracking-widest text-parchment-400 dark:text-earth-400 mb-3 font-medium">
              Platform
            </h2>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {platformOptions.map((opt) => {
                const isSelected = selectedPlatform === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onSelectPlatform(opt.value)}
                    className={`
                      relative flex flex-col items-center gap-1.5 p-3 sm:p-4 rounded-xl transition-all duration-200
                      ${isSelected
                        ? 'bg-ember-500/15 dark:bg-ember-500/10 border-2 border-ember-500/50 dark:border-ember-400/40 text-ember-500 dark:text-ember-400'
                        : 'glass text-parchment-500 dark:text-earth-400 hover:text-parchment-text dark:hover:text-earth-200 hover:bg-black/5 dark:hover:bg-white/5'
                      }
                    `}
                  >
                    {opt.icon}
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="text-[10px] sm:text-xs opacity-60 leading-tight">{opt.sublabel}</span>
                  </button>
                );
              })}
            </div>
            {selectedPlatform === 'apple-music' && APPLE_AFFILIATE_ENABLED && !isAppleMusicAuthorized && (
              <a
                href={getAppleMusicSubscribeUrl('ketaflow_entry')}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 block text-xs text-ember-500 dark:text-ember-400 hover:text-ember-400 dark:hover:text-ember-300 underline text-center"
              >
                Don't have Apple Music? Start your free trial
              </a>
            )}
          </section>

          {/* Treatment Type */}
          <section>
            <h2 className="text-xs uppercase tracking-widest text-parchment-400 dark:text-earth-400 mb-3 font-medium">
              Treatment Type
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {treatmentTypes.map((type) => {
                const isSelected = treatmentType === type;
                const { name, description } = TREATMENT_LABELS[type];
                const shortName = name.split(' (')[0]; // "IV", "IM", "Oral/Sublingual", "Spravato/Intranasal"
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setTreatmentType(type)}
                    className={`
                      flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200
                      ${isSelected
                        ? 'bg-ember-500/15 dark:bg-ember-500/10 border-2 border-ember-500/50 dark:border-ember-400/40 text-ember-500 dark:text-ember-400'
                        : 'glass text-parchment-500 dark:text-earth-400 hover:text-parchment-text dark:hover:text-earth-200 hover:bg-black/5 dark:hover:bg-white/5'
                      }
                    `}
                    title={description}
                  >
                    {treatmentIcons[type]}
                    <span className="text-sm font-medium leading-tight text-center">{shortName}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Duration */}
          <section>
            <h2 className="text-xs uppercase tracking-widest text-parchment-400 dark:text-earth-400 mb-3 font-medium">
              Duration
            </h2>
            <div className="glass rounded-xl p-4 sm:p-5">
              <div className="text-center mb-4">
                {formatDuration(duration).map((part, i) => (
                  <span key={part.unit}>
                    {i > 0 && <span className="inline-block w-2" />}
                    <span className="font-serif text-4xl sm:text-5xl font-semibold text-parchment-text dark:text-earth-100">
                      {part.value}
                    </span>
                    <span className="font-serif text-xl sm:text-2xl text-parchment-400 dark:text-earth-400 ml-1">
                      {part.unit}
                    </span>
                  </span>
                ))}
              </div>
              <input
                type="range"
                min={MIN_DURATION_MINUTES}
                max={MAX_DURATION_MINUTES}
                step={5}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full h-2 bg-black/10 dark:bg-white/10 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-5
                  [&::-webkit-slider-thumb]:h-5
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-ember-500
                  [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:shadow-md
                  [&::-webkit-slider-thumb]:shadow-ember-500/30
                  [&::-moz-range-thumb]:w-5
                  [&::-moz-range-thumb]:h-5
                  [&::-moz-range-thumb]:rounded-full
                  [&::-moz-range-thumb]:bg-ember-500
                  [&::-moz-range-thumb]:border-0
                  [&::-moz-range-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-parchment-300 dark:text-earth-600 mt-1">
                <span>{formatDuration(MIN_DURATION_MINUTES).map(p => `${p.value} ${p.unit}`).join(' ')}</span>
                <span>{formatDuration(MAX_DURATION_MINUTES).map(p => `${p.value} ${p.unit}`).join(' ')}</span>
              </div>
            </div>
          </section>

          {/* Treatment Curve */}
          <section>
            <TreatmentCurveChart
              treatmentType={treatmentType}
              durationMinutes={duration}
              phaseRatios={phaseRatios}
              glowEnabled={visualEffects.glowEffects}
            />
          </section>

          {/* Advanced Settings Toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-parchment-400 dark:text-earth-400 hover:text-parchment-text dark:hover:text-earth-200 transition-colors text-sm"
            >
              <svg
                className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Advanced Settings
            </button>

            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: showAdvanced ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className={`glass rounded-xl p-4 sm:p-6 ${showAdvanced ? 'mt-3' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-parchment-text/80 dark:text-earth-200 font-medium text-sm">Phase Durations</h3>
                    <div className="flex items-center gap-2">
                      {!isValidTotal && (
                        <button
                          type="button"
                          onClick={handleAutoAdjust}
                          className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-300 hover:bg-amber-500/30 transition-colors"
                        >
                          Auto-adjust
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleResetToDefaults}
                        className="text-xs px-2 py-1 rounded bg-black/10 dark:bg-white/10 text-parchment-400 dark:text-earth-400 hover:bg-black/20 dark:hover:bg-white/20 transition-colors"
                      >
                        Reset
                      </button>
                    </div>
                  </div>

                  <div className={`mb-4 flex items-center gap-2 text-sm ${isValidTotal ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {isValidTotal ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Total: {totalPercentage}%</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Total: {totalPercentage}% (should be 100%)</span>
                      </>
                    )}
                  </div>

                  <div className="space-y-4">
                    {PHASES_IN_ORDER.map((phase) => (
                      <div key={phase} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-parchment-text dark:text-earth-100 font-medium text-sm">
                            {PHASE_LABELS[phase]} ({phasePercentages[phase]}%)
                          </span>
                          <span className="text-parchment-400 dark:text-earth-400 text-xs">
                            {getPhaseMinutes(phasePercentages[phase])}
                          </span>
                        </div>
                        <p className="text-parchment-300 dark:text-earth-600 text-xs">{PHASE_DESCRIPTIONS[phase]}</p>
                        <input
                          type="range"
                          min={5}
                          max={60}
                          step={1}
                          value={phasePercentages[phase]}
                          onChange={(e) => handlePhaseChange(phase, Number(e.target.value))}
                          className="w-full h-1.5 bg-black/10 dark:bg-white/10 rounded-full appearance-none cursor-pointer
                            [&::-webkit-slider-thumb]:appearance-none
                            [&::-webkit-slider-thumb]:w-3.5
                            [&::-webkit-slider-thumb]:h-3.5
                            [&::-webkit-slider-thumb]:rounded-full
                            [&::-webkit-slider-thumb]:bg-ember-500
                            [&::-webkit-slider-thumb]:cursor-pointer
                            [&::-webkit-slider-thumb]:shadow-md
                            [&::-moz-range-thumb]:w-3.5
                            [&::-moz-range-thumb]:h-3.5
                            [&::-moz-range-thumb]:rounded-full
                            [&::-moz-range-thumb]:bg-ember-500
                            [&::-moz-range-thumb]:border-0
                            [&::-moz-range-thumb]:cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-500/15 border border-red-500/25 rounded-xl text-red-700 dark:text-red-200 text-center">
              <p className="font-medium">Connection Error</p>
              <p className="text-sm mt-1 text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Begin Session CTA */}
          <Button
            type="submit"
            size="lg"
            isLoading={isLoading}
            disabled={!canSubmit}
            className="w-full"
          >
            {!canSubmit && selectedPlatform === 'none'
              ? 'Select a platform'
              : !canSubmit
                ? 'Adjust phases to 100%'
                : 'Begin Session'}
          </Button>
        </form>
      </div>
    </div>
  );
}
